import { activitiesApi } from "@/api/activities";
import { adminMetricsApi } from "@/api/admin-metrics";
import { categoriesApi } from "@/api/categories";
import { ordersApi } from "@/api/orders";
import { paymentsApi } from "@/api/payments";
import { productsApi } from "@/api/products";
import { reservationSendRequestsApi } from "@/api/reservation-send-requests";
import { reservationsApi } from "@/api/reservations";
import { stationPrintersApi } from "@/api/station-printers";
import { stationTicketsApi } from "@/api/station-tickets";
import { tablesApi } from "@/api/tables";
import { zonesApi } from "@/api/zones";
import type {
  ActivityLog,
  Category,
  Order,
  Payment,
  Product,
  Table,
  Zone,
} from "@/lib/pos-types";
import { customersApi } from "./costumers";

type ProductCreatePayload = {
  name: string;
  description?: string;
  price: number;
  categoryId: string;
  available?: boolean;
  preparationTime?: number | null;
  imageFile?: File | null;
  availabilityRules?: { days: number[]; start: string; end: string }[];
  priceRules?: { days: number[]; start: string; end: string; price: number }[];
};

type ProductUpdatePayload = Record<string, unknown> & {
  imageFile?: File | null;
};

type CategoryCreatePayload = { name: string; sortOrder?: number };
type CategoryUpdatePayload = { name?: string; sortOrder?: number };

/**
 * Fachada de API para el dominio POS.
 *
 * Motivación:
 * - Evitar que la UI conozca el detalle de cada módulo `/api/*`.
 * - Mantener un “punto único” de llamadas de negocio (tables/orders/payments/etc.).
 *
 * Nota:
 * - En general, los módulos de `src/api/*` normalizan respuestas y shapes.
 * - Este archivo sólo agrega un layer de agrupación y tipado.
 */
export const posApi = {
  listTables: (): Promise<Table[]> => tablesApi.list(),
  reservationUnlockTable: (tableId: string, code: string): Promise<void> =>
    tablesApi.reservationUnlock(tableId, code),
  unlockTable: (tableId: string, code: string): Promise<void> =>
    tablesApi.unlock(tableId, code),
  transferTable: (tableId: string, destinationTableId: string): Promise<void> =>
    tablesApi.transfer(tableId, destinationTableId),
  listOrders: (
    params?: Parameters<typeof ordersApi.list>[0],
  ): Promise<Order[]> => ordersApi.list(params),
  listProducts: (): Promise<Product[]> => productsApi.list(),
  listCategories: (): Promise<Category[]> => categoriesApi.list(),
   listCustomers: (): Promise<import("@/lib/pos-types").Customer[]> =>
    customersApi.list(),
  createCustomer: (
    payload: Parameters<typeof customersApi.create>[0],
  ): Promise<import("@/lib/pos-types").Customer> =>
    customersApi.create(payload),
  updateCustomer: (
    id: string,
    payload: Parameters<typeof customersApi.update>[1],
  ): Promise<import("@/lib/pos-types").Customer> =>
    customersApi.update(id, payload),
  deleteCustomer: (id: string): Promise<void> => customersApi.delete(id),

  listReservations: (
    params?: Parameters<typeof reservationsApi.list>[0],
  ): Promise<import("@/lib/pos-types").Reservation[]> =>
    reservationsApi.list(params),
  createReservation: (
    payload: Parameters<typeof reservationsApi.create>[0],
  ): Promise<import("@/lib/pos-types").Reservation> =>
    reservationsApi.create(payload),
  updateReservation: (
    id: string,
    payload: Parameters<typeof reservationsApi.update>[1],
  ): Promise<import("@/lib/pos-types").Reservation> =>
    reservationsApi.update(id, payload),
  listReservationSendRequests: (
    params?: Parameters<typeof reservationSendRequestsApi.list>[0],
  ) => reservationSendRequestsApi.list(params),
  createReservationSendRequest: (
    payload: Parameters<typeof reservationSendRequestsApi.create>[0],
  ) => reservationSendRequestsApi.create(payload),
  approveReservationSendRequest: (id: string) =>
    reservationSendRequestsApi.approve(id),
  rejectReservationSendRequest: (id: string) =>
    reservationSendRequestsApi.reject(id),
  listActivities: (
    params?: Parameters<typeof activitiesApi.list>[0],
  ): Promise<ActivityLog[]> => activitiesApi.list(params),
  listPayments: (): Promise<Payment[]> => paymentsApi.list(),
  listZones: (): Promise<Zone[]> => zonesApi.list(),
  listStationPrinters: () => stationPrintersApi.list(),
  testStationPrinter: (
    stationKey: Parameters<typeof stationPrintersApi.testPrint>[0],
  ) => stationPrintersApi.testPrint(stationKey),
  createZone: (payload: unknown): Promise<Zone> => zonesApi.create(payload),
  updateZone: (zoneId: string, payload: unknown): Promise<Zone> =>
    zonesApi.update(zoneId, payload),
  deleteZone: (zoneId: string): Promise<void> => zonesApi.delete(zoneId),
  createTable: (payload: unknown): Promise<Table> => tablesApi.create(payload),
  deleteTable: (tableId: string): Promise<void> => tablesApi.delete(tableId),
  createOrder: (payload: unknown): Promise<Order> => ordersApi.create(payload),
  updateOrder: (orderId: string, payload: unknown): Promise<Order> =>
    ordersApi.update(orderId, payload),
  deleteOrder: (orderId: string): Promise<void> => ordersApi.delete(orderId),
  updateTable: (tableId: string, payload: unknown): Promise<Table> =>
    tablesApi.update(tableId, payload),
  createProduct: (payload: ProductCreatePayload): Promise<Product> =>
    productsApi.create(payload),
  updateProduct: (
    productId: string,
    payload: ProductUpdatePayload,
  ): Promise<Product> => productsApi.update(productId, payload),
  deleteProduct: (productId: string): Promise<void> =>
    productsApi.delete(productId),
  createCategory: (payload: CategoryCreatePayload): Promise<Category> =>
    categoriesApi.create(payload),
  updateCategory: (
    categoryId: string,
    payload: CategoryUpdatePayload,
  ): Promise<Category> => categoriesApi.update(categoryId, payload),
  deleteCategory: (categoryId: string): Promise<void> =>
    categoriesApi.delete(categoryId),
  createPayment: (payload: unknown): Promise<Payment> =>
    paymentsApi.create(payload),
  updateStationPrinter: (
    stationKey: Parameters<typeof stationPrintersApi.update>[0],
    payload: Parameters<typeof stationPrintersApi.update>[1],
  ) => stationPrintersApi.update(stationKey, payload),
  markStationTicketReady: (ticketId: string) =>
    stationTicketsApi.markReady(ticketId),
  reprintStationTicket: (ticketId: string) =>
    stationTicketsApi.reprint(ticketId),
  listStationTickets: (params?: Parameters<typeof stationTicketsApi.list>[0]) =>
    stationTicketsApi.list(params),
  getAdminMetrics: (params?: Parameters<typeof adminMetricsApi.get>[0]) =>
    adminMetricsApi.get(params),
};
